import React, { useState, useEffect } from 'react';
import { Activity, RefreshCw, Loader, ChevronDown, ChevronUp } from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { useAuth } from '../../../../contexts/AuthContext';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { stcBinService } from '../../../../services/stcBinService';
import { locationHeatmapService } from '../../../../services/locationHeatmapService';
import { RackSummary, RackStructure } from '../rack-builder/types';
import { LocationHeatmapDto, HeatmapFilterState } from './types';
import { HeatmapFilterPanel } from './HeatmapFilterPanel';
import { HeatmapRackGrid } from './HeatmapRackGrid';

/**
 * Location Heatmap Application
 *
 * Displays warehouse rack locations with heat colors based on activity levels
 * Optimized for large screens to display maximum locations
 */

interface LocationHeatmapAppProps {
  onBack?: () => void;
}

export const LocationHeatmapApp: React.FC<LocationHeatmapAppProps> = ({ onBack }) => {
    const { user } = useAuth();
    const { currentWarehouse } = useWarehouse();
    const [racks, setRacks] = useState<RackSummary[]>([]);
    const [selectedRackNumber, setSelectedRackNumber] = useState<string | null>(null);
    const [rackStructure, setRackStructure] = useState<RackStructure | null>(null);
    const [heatmapData, setHeatmapData] = useState<Map<string, LocationHeatmapDto>>(new Map());
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingHeatmap, setIsLoadingHeatmap] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isRackPanelExpanded, setIsRackPanelExpanded] = useState(true);

    const companyNo = user?.companyNo || '';
    const warehouseID = currentWarehouse || '1';

    // Default filter state - last 30 days
    const getDefaultFilterState = (): HeatmapFilterState => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);

        return {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
            transType: null,
            includeWeekends: false,
            includeHolidays: false
        };
    };

    const [filterState, setFilterState] = useState<HeatmapFilterState>(getDefaultFilterState());

    // Load racks on mount
    useEffect(() => {
        loadRacks();
    }, [companyNo, warehouseID]);

    /**
     * Load all racks for warehouse
     */
    const loadRacks = async () => {
        if (!companyNo) return;
        
        setIsLoading(true);
        setError(null);
        
        try {
            const data = await stcBinService.getRackSummary(companyNo, warehouseID);
            setRacks(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load racks');
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Load rack structure when rack is selected
     */
    const loadRackStructure = async (rackNumber: string) => {
        if (!companyNo) return;

        setIsLoading(true);
        setError(null);
        
        try {
            const structure = await stcBinService.getRackStructure(companyNo, warehouseID, rackNumber);
            setRackStructure(structure);
            
            // Automatically load heatmap data for the selected rack
            await loadHeatmapData(rackNumber);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load rack structure');
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Load heatmap data
     */
    const loadHeatmapData = async (rackNumber?: string) => {
        const targetRack = rackNumber || selectedRackNumber;
        if (!targetRack) return;

        setIsLoadingHeatmap(true);
        setError(null);
        
        try {
            // Get location prefix from rack number (first 2 characters)
            const locationPrefix = targetRack.substring(0, 2);

            const request = {
                startDate: filterState.startDate + 'T00:00:00',
                endDate: filterState.endDate + 'T23:59:59',
                locationPrefix: locationPrefix,
                transType: filterState.transType,
                includeWeekends: filterState.includeWeekends,
                includeHolidays: filterState.includeHolidays
            };

            const data = await locationHeatmapService.getHeatmapAnalysis(request);
            
            // Convert array to Map for quick lookup
            const dataMap = new Map<string, LocationHeatmapDto>();
            data.forEach(item => {
                dataMap.set(item.binCode, item);
            });
            
            setHeatmapData(dataMap);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load heatmap data');
        } finally {
            setIsLoadingHeatmap(false);
        }
    };

    /**
     * Handle rack selection
     */
    const handleRackSelect = (rackNumber: string) => {
        setSelectedRackNumber(rackNumber);
        loadRackStructure(rackNumber);
    };

    /**
     * Reset filters to default
     */
    const handleResetFilters = () => {
        setFilterState(getDefaultFilterState());
    };

    /**
     * Apply filters - reload heatmap data
     */
    const handleApplyFilters = () => {
        if (selectedRackNumber) {
            loadHeatmapData();
        }
    };

    return (
        <div className="flex flex-col h-screen w-full overflow-hidden bg-gray-50">
            {/* Toolbar - Compact */}
            <div className="flex items-center justify-between bg-white px-4 py-2 border-b border-gray-200 shadow-sm flex-shrink-0">
                <div className="flex items-center space-x-3">
                    <BackButton onClick={onBack || (() => {})} />
                    <Activity className="w-5 h-5 text-blue-600" />
                    <div>
                        <h1 className="text-lg font-bold text-gray-800">Lokasjon HeatMap</h1>
                        <p className="text-xs text-gray-600">
                            Lager: {warehouseID} | Selskap: {companyNo}
                        </p>
                    </div>
                </div>
                
                <div className="flex items-center space-x-2">
                    <button
                        onClick={loadRacks}
                        disabled={isLoading}
                        className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                        <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                        <span>Oppdater</span>
                    </button>
                </div>
            </div>

            {/* Error message */}
            {error && (
                <div className="bg-red-50 border-b border-red-200 text-red-800 px-4 py-2 text-sm flex-shrink-0">
                    {error}
                </div>
            )}

            {/* Rack selector - COLLAPSIBLE RIBBON STYLE */}
            <div className="bg-white border-b border-gray-200 flex-shrink-0">
                {/* Ribbon header */}
                <div 
                    className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setIsRackPanelExpanded(!isRackPanelExpanded)}
                >
                    <div className="flex items-center space-x-3">
                        <h2 className="text-sm font-semibold text-gray-800">
                            Velg Reol
                        </h2>
                        {selectedRackNumber && (
                            <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                                Reol {selectedRackNumber}
                            </span>
                        )}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                        {selectedRackNumber && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedRackNumber(null);
                                    setRackStructure(null);
                                    setHeatmapData(new Map());
                                }}
                                className="text-xs text-gray-600 hover:text-gray-800 underline"
                            >
                                Fjern valg
                            </button>
                        )}
                        {isRackPanelExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-500" />
                        ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                        )}
                    </div>
                </div>

                {/* Rack grid - collapsible */}
                {isRackPanelExpanded && (
                    <div className="px-4 pb-3 border-t border-gray-100 animate-in slide-in-from-top duration-200">
                        {isLoading && racks.length === 0 ? (
                            <div className="flex items-center justify-center py-4">
                                <Loader className="w-5 h-5 animate-spin text-blue-600" />
                                <span className="ml-2 text-sm text-gray-600">Laster reoler...</span>
                            </div>
                        ) : racks.length === 0 ? (
                            <div className="text-center py-4 text-sm text-gray-500">
                                Ingen reoler funnet.
                            </div>
                        ) : (
                            <div className="grid grid-cols-4 md:grid-cols-8 lg:grid-cols-12 xl:grid-cols-16 gap-2 mt-3">
                                {racks.map(rack => (
                                    <button
                                        key={rack.rackNumber}
                                        onClick={() => handleRackSelect(rack.rackNumber)}
                                        className={`p-2 rounded border-2 transition-all text-left ${
                                            selectedRackNumber === rack.rackNumber
                                                ? 'bg-blue-100 border-blue-500 shadow-sm'
                                                : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow'
                                        }`}
                                    >
                                        <div className="text-sm font-bold text-gray-800">
                                            Reol {rack.rackNumber}
                                        </div>
                                        <div className="text-xs text-gray-600">
                                            {rack.locationCount} lok
                                        </div>
                                        <div className="text-[10px] text-gray-500">
                                            V{rack.minVertical}-{rack.maxVertical} × H{rack.minShelf}-{rack.maxShelf}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Main content area - 2 columns */}
            {selectedRackNumber && rackStructure && (
                <div 
                    className="flex-1 min-h-0 flex gap-2 p-2" 
                    style={{ maxHeight: isRackPanelExpanded ? 'calc(100vh - 240px)' : 'calc(100vh - 160px)' }}
                >
                    {/* Filter panel (Left) */}
                    <div className="w-64 bg-white rounded-lg shadow flex flex-col flex-shrink-0">
                        <div className="bg-white border-b border-gray-200 px-3 py-2 flex-shrink-0">
                            <h3 className="font-semibold text-sm text-gray-800">Filtrer periode</h3>
                        </div>
                        <div className="flex-1 overflow-y-auto p-3">
                            <HeatmapFilterPanel
                                filterState={filterState}
                                onFilterChange={setFilterState}
                                onReset={handleResetFilters}
                                onApply={handleApplyFilters}
                                isLoading={isLoadingHeatmap}
                            />
                        </div>
                    </div>

                    {/* Heatmap grid (Center) */}
                    <div className="flex-1 bg-white rounded-lg shadow flex flex-col min-w-0">
                        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 flex-shrink-0">
                            <h3 className="font-semibold text-sm text-gray-800">
                                Aktivitetskart - Reol {selectedRackNumber}
                            </h3>
                            {heatmapData.size > 0 && (
                                <span className="text-xs text-gray-600">
                                    {heatmapData.size} lokasjoner med aktivitet
                                </span>
                            )}
                        </div>
                        
                        <div className="flex-1 overflow-hidden p-2">
                            {isLoading || isLoadingHeatmap ? (
                                <div className="flex items-center justify-center h-full">
                                    <Loader className="w-6 h-6 animate-spin text-blue-600" />
                                    <span className="ml-2 text-sm text-gray-600">
                                        {isLoading ? 'Laster reol...' : 'Laster heatmap data...'}
                                    </span>
                                </div>
                            ) : (
                                <HeatmapRackGrid
                                    rackStructure={rackStructure}
                                    heatmapData={heatmapData}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* No rack selected message */}
            {!selectedRackNumber && !isLoading && (
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center text-gray-500">
                        <Activity className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p className="text-lg font-medium">Velg en reol for å vise aktivitetskart</p>
                        <p className="text-sm mt-2">Klikk på en reol ovenfor for å se aktivitetsnivået</p>
                    </div>
                </div>
            )}
        </div>
    );
};
